import {
  bbox as boundingBox,
  bboxPolygon,
  centroid,
  destination,
  distance,
  featureCollection,
  getType,
  point,
  transformRotate,
  transformTranslate,
} from '@turf/turf';
import { Feature, FeatureCollection, Point, Polygon } from 'geojson';
import {
  DEFAULT_BETWEEN_ROW_SPACING,
  DEFAULT_IN_ROW_SPACING,
  geometryTypes,
  OFFSET_MAX_FEET,
} from '../../constants/fields';
import { getTotalPatternFt } from '../patterns';
import { feetPerMile } from '../../constants/units';
import {
  DataMaps,
  GridProps,
  PointsAndRowsProps,
} from '../../types/pointsAndRows';
import { unitsMiles } from '../units';

interface GenerateGridPointsProps extends GridProps, PointsAndRowsProps {
  fieldFeature?: Feature<Polygon>;
}

// Search keywords: cropify, addRowsAndPoints, addPointsAndRows
// Based on https://github.com/ikendoit/turf-pointGridDensity
const generateGridPoints = (
  {
    betweenRowSpacing = DEFAULT_BETWEEN_ROW_SPACING,
    fieldFeature,
    inRowSpacing = DEFAULT_IN_ROW_SPACING,
    offsetX = 0,
    offsetY = 0,
    primaryInRowPattern,
    rotation = 0,
  }: GenerateGridPointsProps,
  { inRowPatternsMap }: DataMaps,
): { points: FeatureCollection<Point>; rotationCenter: Feature<Point> } => {
  // Input Validation
  if (!fieldFeature) throw new Error('Requires fieldFeature');
  if (!betweenRowSpacing) throw new Error('Requires betweenRowSpacing');
  if (!inRowSpacing && !primaryInRowPattern)
    throw new Error('Requires inRowSpacing or primaryInRowPattern');
  if (getType(fieldFeature) !== geometryTypes.POLYGON) {
    throw new Error('fieldFeature must be a Polygon');
  }

  const bbox = boundingBox(fieldFeature);
  const rotationCenter = centroid(bboxPolygon(bbox));

  const sectionPattern =
    primaryInRowPattern && inRowPatternsMap[primaryInRowPattern.id];

  // Uses the pattern length in ft as in-row spacing if a primary pattern is present.
  const effectiveInRowSpacing = sectionPattern
    ? getTotalPatternFt(sectionPattern)
    : inRowSpacing;

  // Resize bounding box to account for rotation.
  // Padding ensures that at least one point is generated outside the
  // bounding box in each row so the row end computations won't span
  // multiple rows.
  const padding = (effectiveInRowSpacing * 1.5) / feetPerMile;
  const radius =
    distance([bbox[0], bbox[1]], [bbox[2], bbox[3]]) / 2 +
    OFFSET_MAX_FEET / feetPerMile;
  const paddedRadius = radius + padding;
  const centerCoords = rotationCenter.geometry.coordinates;
  const [west] = destination(centerCoords, radius, -90).geometry.coordinates;
  const [east] = destination(centerCoords, radius, 90).geometry.coordinates;
  const [, north] = destination(
    centerCoords,
    paddedRadius,
    0,
  ).geometry.coordinates;
  const [, south] = destination(
    centerCoords,
    paddedRadius,
    180,
  ).geometry.coordinates;

  // Compute distances
  const xFraction =
    betweenRowSpacing /
    feetPerMile /
    distance([west, south], [east, south], unitsMiles);
  const cellWidth = xFraction * (east - west);

  const yFraction =
    effectiveInRowSpacing /
    feetPerMile /
    distance([west, south], [west, north], unitsMiles);
  const cellHeight = yFraction * (north - south);

  const bboxWidth = east - west;
  const bboxHeight = north - south;

  const columns = Math.floor(bboxWidth / cellWidth);
  const rows = Math.floor(bboxHeight / cellHeight);
  // Adjust the origin of the grid
  const deltaX = (bboxWidth - columns * cellWidth) / 2;
  const deltaY = (bboxHeight - rows * cellHeight) / 2;

  // Generate point grid
  const pointsArr = [];
  let currentX = west + deltaX;
  while (currentX <= east) {
    let currentY = south + deltaY;
    while (currentY <= north) {
      const cellPt = point([currentX, currentY]);
      pointsArr.push(cellPt);
      currentY += cellHeight;
    }
    currentX += cellWidth;
  }

  // TODO rotate around feature center point
  // Rotate point grid
  let points = transformRotate(featureCollection(pointsArr), rotation);
  // Shift point grid
  points = transformTranslate(points, offsetX / feetPerMile, 90, unitsMiles);
  points = transformTranslate(points, offsetY / feetPerMile, 0, unitsMiles);
  return { points, rotationCenter };
};

export default generateGridPoints;
